/**
 * Editor for one VariableDefinition.
 *
 * Used by the new-prompt wizard (Step 2 — "Define unknowns") AND the
 * prompt detail page's "Definitions" section. Operator picks "Fixed"
 * (one value for every persona) or "Per persona" (one value per
 * persona). Per-persona mode requires the personas list so the
 * operator can fill each row.
 */
import { useState } from 'react';

import { Spinner } from '@/components/Spinner';
import {
  type PersonaListItem,
  type VariableDefinition,
  type VariableDefinitionKind,
} from '@/lib/api';
import { cn } from '@/lib/utils';

type Props = {
  variable: string;
  personas: PersonaListItem[];
  loadingPersonas: boolean;
  value: VariableDefinition;
  onChange: (v: VariableDefinition) => void;
};

export function DefinitionEditor({
  variable,
  personas,
  loadingPersonas,
  value,
  onChange,
}: Props) {
  const isComplete = checkComplete(value, personas);
  return (
    <div
      className={cn(
        'rounded-md border bg-card p-4 shadow-sm',
        isComplete ? 'border-success/30' : 'border-status-error-border',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <code className="font-mono text-sm font-medium">
          {`{${variable}}`}
        </code>
        <span
          className={cn(
            'text-xs font-medium',
            isComplete ? 'text-success' : 'text-status-error-text',
          )}
        >
          {isComplete ? 'defined' : 'needs definition'}
        </span>
      </div>

      <Field
        label="Notes"
        help="Your own description — for the audit trail. Does NOT control how the value is generated."
      >
        <input
          value={value.description}
          onChange={(e) =>
            onChange({ ...value, description: e.target.value })
          }
          placeholder="Optional: what does this variable represent?"
          className={inputCls}
        />
      </Field>

      <Field label="How should this variable be filled at run time?">
        <div className="grid gap-1 sm:grid-cols-3 rounded-md border border-border bg-muted p-1">
          <KindButton
            active={value.kind === 'fixed'}
            onClick={() =>
              onChange({ ...value, kind: 'fixed' as VariableDefinitionKind })
            }
          >
            Fixed
            <span className="block text-[10px] font-normal text-muted-foreground">
              Same value for every persona
            </span>
          </KindButton>
          <KindButton
            active={value.kind === 'per_persona'}
            onClick={() =>
              onChange({
                ...value,
                kind: 'per_persona' as VariableDefinitionKind,
              })
            }
          >
            Per persona
            <span className="block text-[10px] font-normal text-muted-foreground">
              Each persona gets its own value
            </span>
          </KindButton>
          <KindButton
            active={value.kind === 'skip'}
            onClick={() =>
              onChange({ ...value, kind: 'skip' as VariableDefinitionKind })
            }
          >
            Skip
            <span className="block text-[10px] font-normal text-muted-foreground">
              Leave blank — substitute with whitespace
            </span>
          </KindButton>
        </div>
      </Field>

      {value.kind === 'fixed' && (
        <Field label="Value (used for every run, every persona)">
          <input
            value={value.fixed_value ?? ''}
            onChange={(e) =>
              onChange({ ...value, fixed_value: e.target.value })
            }
            placeholder="Type a real value, not a placeholder like X / TBD"
            className={inputCls}
          />
        </Field>
      )}

      {value.kind === 'per_persona' && (
        <Field
          label={`Value per persona (${personas.length} personas)`}
          help="Type a value for each. Run is blocked until every one has a non-placeholder value."
        >
          {loadingPersonas ? (
            <Spinner />
          ) : personas.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              No personas uploaded for this cell yet.
            </div>
          ) : (
            <PerPersonaTable
              personas={personas}
              values={value.per_persona_values}
              onChange={(per_persona_values) =>
                onChange({ ...value, per_persona_values })
              }
            />
          )}
        </Field>
      )}

      {value.kind === 'skip' && (
        <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          The <code className="font-mono">{`{${variable}}`}</code> placeholder will be replaced with a single space wherever it appears in the prompt body. Use this when the variable shows up in the body but doesn't actually need a real value for the test.
        </div>
      )}
    </div>
  );
}

function PerPersonaTable({
  personas,
  values,
  onChange,
}: {
  personas: PersonaListItem[];
  values: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const filled = personas.filter((p) =>
    isRealValue(values[p.persona_id]),
  ).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {filled} / {personas.length} filled
        </span>
        <BulkFill
          personas={personas}
          values={values}
          onChange={onChange}
        />
      </div>
      <div className="max-h-72 overflow-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-left uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Persona</th>
              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {personas.map((p) => {
              const v = values[p.persona_id] ?? '';
              const empty = !isRealValue(v);
              return (
                <tr key={p.persona_id} className="border-t border-border">
                  <td className="px-3 py-1.5">
                    <div className="font-mono text-[11px]">{p.persona_id}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.name}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      value={v}
                      onChange={(e) =>
                        onChange({
                          ...values,
                          [p.persona_id]: e.target.value,
                        })
                      }
                      placeholder="value"
                      className={cn(
                        'h-7 w-full rounded border border-input bg-card px-2 text-xs',
                        empty && 'border-status-error-border bg-status-error/30',
                      )}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkFill({
  personas,
  values,
  onChange,
}: {
  personas: PersonaListItem[];
  values: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const [bulk, setBulk] = useState('');
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-muted-foreground underline-offset-2 hover:underline"
      >
        bulk-fill empties
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        value={bulk}
        onChange={(e) => setBulk(e.target.value)}
        placeholder="value for every empty row"
        className="h-7 rounded border border-input bg-card px-2 text-xs"
      />
      <button
        onClick={() => {
          if (!isRealValue(bulk)) return;
          const next = { ...values };
          for (const p of personas) {
            if (!isRealValue(next[p.persona_id])) {
              next[p.persona_id] = bulk;
            }
          }
          onChange(next);
          setBulk('');
          setOpen(false);
        }}
        disabled={!isRealValue(bulk)}
        className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
      >
        apply
      </button>
      <button
        onClick={() => setOpen(false)}
        className="text-muted-foreground"
      >
        ×
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────

const PLACEHOLDERS = new Set([
  '',
  'x',
  'tbd',
  '?',
  'todo',
  'fixme',
  '...',
  'n/a',
  'na',
  'placeholder',
]);

export function isRealValue(value: string | null | undefined): boolean {
  if (value == null) return false;
  return !PLACEHOLDERS.has(value.trim().toLowerCase());
}

export function checkComplete(
  def: VariableDefinition,
  personas: PersonaListItem[],
): boolean {
  // Skip is always complete — operator explicitly chose to leave blank.
  if (def.kind === 'skip') return true;
  if (def.kind === 'fixed') return isRealValue(def.fixed_value);
  return personas.every((p) =>
    isRealValue(def.per_persona_values[p.persona_id]),
  );
}

export function emptyDefinition(name: string): VariableDefinition {
  return {
    name,
    kind: 'fixed',
    description: '',
    fixed_value: '',
    per_persona_values: {},
  };
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mt-3 flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
    </label>
  );
}

function KindButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 rounded-md px-3 py-1.5 text-left text-xs font-medium transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

const inputCls = cn(
  'h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
);
